import { useEffect, useState } from "react";
import { Radio } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, EmptyState } from "@/components/ranger/PageHeader";
import { WarnDialog } from "@/components/ranger/WarnDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const dur = (s) => (s == null ? "—" : s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m` : `${Math.floor(s / 60)}m`);

const PlayerRows = ({ players, cid, canWarn, onWarned }) => (
  <div className="overflow-x-auto">
    <Table data-testid="online-players-table">
      <TableHeader><TableRow className="border-[#1E2D4A]">{["Player", "Linked Discord", "Online for", "Ping", "Health", "Warnings", ""].map((h) => <TableHead key={h} className="rg-label whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {players.map((p) => (
          <TableRow key={p.name} data-testid={`online-player-${p.name}`} className="border-[#1E2D4A]/60 text-xs">
            <TableCell className="font-display text-base font-semibold text-slate-100">{p.name}</TableCell>
            <TableCell>{p.linked ? (
              <span className="text-[#E5202E]">{p.linked.discord_username} <span className="font-mono uppercase text-slate-500">· {p.linked.platform}</span></span>
            ) : <span className="text-slate-500">Not linked</span>}</TableCell>
            <TableCell className="font-mono text-slate-300">{dur(p.connected_seconds)}</TableCell>
            <TableCell className="font-mono text-slate-300">{p.ping ?? "—"}</TableCell>
            <TableCell className="font-mono text-slate-300">{p.health != null ? Math.round(p.health) : "—"}</TableCell>
            <TableCell data-testid={`online-player-warnings-${p.name}`} className={`font-mono ${p.warnings ? "text-amber-400" : "text-slate-500"}`}>{p.warnings ?? 0}</TableCell>
            <TableCell>{canWarn && <WarnDialog cid={cid} gamertag={p.name} onDone={onWarned} />}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const OnlinePlayers = ({ cid }) => {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    setData(null);
    const load = () => api.get(`/communities/${cid}/players/online`)
      .then((r) => { if (live) { setData(r.data); setError(""); } })
      .catch((e) => live && setError(errMsg(e)));
    load();
    const t = setInterval(load, 15000);
    return () => { live = false; clearInterval(t); };
  }, [cid, tick]);

  const right = data && <span data-testid="online-players-count" className="font-mono text-xs text-slate-400">{data.count} online · {new Date(data.fetched_at).toLocaleTimeString()}</span>;
  return (
    <Card title="Online now" icon={Radio} right={right} testId="online-players-card" className="mb-5">
      {error ? <p data-testid="online-players-error" className="font-mono text-xs text-amber-400">{error}</p>
        : !data ? <p className="font-mono text-xs text-slate-500">Asking the server…</p>
        : data.count === 0 ? <EmptyState title="Nobody online" testId="online-players-empty">The server reports no connected players right now.</EmptyState>
        : <PlayerRows players={data.players} cid={cid} canWarn={can("moderator")} onWarned={() => setTimeout(() => setTick((x) => x + 1), 11000)} />}
    </Card>
  );
};
